import { API_CONFIG } from "@/infrastructure/config/api";
import { delApi, getApi, postApi, putApi } from "@/shared/utils/fetchApi";
import type {
  Job,
  JobDetail,
  JobInput,
  JobReferences,
  JobRun,
  JobRunsPage,
  JobRunsQuery,
  JobWriteResult,
} from "../types/job";

/**
 * Tela Jobs (AB#86605, AC13). As rotas `/jobs` ficam na raiz da api, como as de
 * skills — um caminho relativo herdaria o prefixo `/api` do `fetchApi` e daria
 * 404. O workspace viaja como `product_id`; a organização e o papel saem da
 * sessão, e a api recusa a escrita de quem não é admin.
 */
const ROOT = `${API_CONFIG.API_ROOT_URL}/jobs`;

function withWorkspace(path: string, productId: string, extra: Record<string, string> = {}): string {
  return `${ROOT}${path}?${new URLSearchParams({ product_id: productId, ...extra }).toString()}`;
}

export class JobsService {
  static async list(productId: string): Promise<Job[]> {
    const data = await getApi<{ jobs: Job[] }>(withWorkspace("", productId));
    return data.jobs ?? [];
  }

  /**
   * Um job, com o webhook dele (endereço, usuário e senha para quem administra).
   * Um workflow que não é job (tem mais passos) responde 409 `not_a_job`.
   */
  static get(productId: string, id: string): Promise<JobDetail> {
    return getApi(withWorkspace(`/${encodeURIComponent(id)}`, productId));
  }

  /** Skills e campos do contexto do workspace que a instrução pode citar com `@`. */
  static references(productId: string): Promise<JobReferences> {
    return getApi<JobReferences>(withWorkspace("/references", productId));
  }

  static create(productId: string, input: JobInput): Promise<JobWriteResult> {
    return postApi<JobWriteResult>(ROOT, { ...input, product_id: productId });
  }

  static update(productId: string, id: string, input: Partial<JobInput>): Promise<JobWriteResult> {
    return putApi<JobWriteResult>(`${ROOT}/${encodeURIComponent(id)}`, { ...input, product_id: productId });
  }

  static test(productId: string, id: string): Promise<{ execution_id: string; status: string }> {
    return postApi(`${ROOT}/${encodeURIComponent(id)}/test`, { product_id: productId });
  }

  static publish(productId: string, id: string): Promise<{ id: string; status: string; version: string }> {
    return postApi(`${ROOT}/${encodeURIComponent(id)}/publish`, { product_id: productId });
  }

  /** Exclui o job — o uninstall da Danger Zone dos flows, com histórico e credencial do webhook. Sem volta. */
  static remove(productId: string, id: string): Promise<{ id: string; status: "removed" }> {
    return delApi(withWorkspace(`/${encodeURIComponent(id)}`, productId));
  }

  /** O histórico geral: as execuções de todos os jobs do workspace, da mais recente. */
  static runs(productId: string, query: JobRunsQuery, page: number, pageSize: number): Promise<JobRunsPage> {
    const extra: Record<string, string> = {
      status: query.status,
      period: query.period,
      page: String(page),
      page_size: String(pageSize),
    };
    if (query.jobIds.length > 0) extra.job_id = query.jobIds.join(",");
    if (query.q.trim()) extra.q = query.q.trim();
    return getApi<JobRunsPage>(withWorkspace("/runs", productId, extra));
  }

  static run(productId: string, executionId: string): Promise<JobRun> {
    return getApi<JobRun>(withWorkspace(`/runs/${encodeURIComponent(executionId)}`, productId));
  }
}
